import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    department: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { email, password, confirmPassword, displayName } = formData;

    if (!email || !password || !displayName) {
      setError('필수 항목을 모두 입력해 주세요.');
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await register({
        email,
        password,
        displayName,
        department: formData.department,
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(`회원가입에 실패했습니다: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: { xs: 2, md: 4 },
        py: { xs: 8, md: 10 },
        backgroundImage:
          'radial-gradient(circle at 12% 20%, rgba(90, 125, 255, 0.18), transparent 55%), radial-gradient(circle at 85% 10%, rgba(125, 159, 255, 0.24), transparent 45%), linear-gradient(160deg, #101d3c 0%, #142650 40%, #1d3c78 100%)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 4,
          p: { xs: 3.5, md: 4 },
          border: `1px solid ${alpha(theme.palette.primary.light, 0.28)}`,
          backgroundImage: `linear-gradient(155deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(
            theme.palette.primary.dark,
            0.32,
          )})`,
          boxShadow: `0 28px 48px ${alpha(theme.palette.primary.dark, 0.32)}`,
          backdropFilter: 'blur(16px)',
        }}
      >
        <Stack spacing={0.75} textAlign="center" mb={3}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: '0.32em', color: alpha(theme.palette.text.secondary, 0.85) }}
          >
            EGMMS V3
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            신규 사용자 등록
          </Typography>          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.85) }}>
            조직 정보를 입력해 스마트 프로젝트 관리 시스템을 바로 시작하세요.
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            required
            fullWidth
            name="displayName"
            label="이름"
            autoFocus
            value={formData.displayName}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            name="email"
            label="이메일"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />          <TextField
            fullWidth
            name="department"
            label="부서 (선택)"
            value={formData.department}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            name="password"
            label="비밀번호"
            type="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            name="confirmPassword"
            label="비밀번호 확인"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.25,
              fontWeight: 700,
              letterSpacing: '0.08em',
            }}
          >
            {loading ? '등록 중...' : '계정 생성'}
          </Button>
          <Stack direction="row" justifyContent="center" spacing={1} mt={0.5}>
            <Typography variant="body2" color={alpha(theme.palette.text.secondary, 0.8)}>
              이미 계정이 있나요?
            </Typography>
            <Link
              component={RouterLink}
              to="/login"
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.secondary.light }}
            >
              로그인
            </Link>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;